import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Modal, Select } from 'antd';
import { AddDesignationIcon, CloseIcon } from '../../../assets/icons';

/* ------------------------------------------------------------------ *
 * Add Designation modal (Create Government Department, Figma 1691-1005).
 * Collects a post's title, numeric rank, control mode, type and terminal
 * flag. Escalation is derived automatically from rank, so no field for it.
 * ------------------------------------------------------------------ */

/** A post's control mode within the department ('None' = no control role). */
export type ControlMode = 'Primary' | 'Secondary' | 'None';
/** A post's nature — an elected office or an appointed official post. */
export type DesignationType = 'Elected' | 'Official';

export const CONTROL_MODES: ControlMode[] = ['Primary', 'Secondary', 'None'];
export const DESIGNATION_TYPES: DesignationType[] = ['Elected', 'Official'];

/** Control-mode pill palettes (shared with the designations table). */
export const CONTROL_MODE_TONES: Record<ControlMode, string> = {
  Primary: 'bg-[#eff6ff] border-[#d1e2ff] text-[#0061ff]',
  Secondary: 'bg-[#f1f5f9] border-[#e2e8f0] text-[#334155]',
  None: 'bg-[#f8fafc] border-[#e2e8f0] text-[#94a3b8]',
};

/** Authority pill palettes (shared with the designations table). */
export const DESIGNATION_TYPE_TONES: Record<DesignationType, string> = {
  Elected: 'bg-[#eff6ff] border-[#d1e2ff] text-[#0061ff]',
  Official: 'bg-[#f1f5f9] border-[#e2e8f0] text-[#334155]',
};

export interface NewDesignation {
  name: string;
  rank: number;
  controlMode: ControlMode;
  type: DesignationType;
  terminal: boolean;
}

interface DesignationForm {
  name: string;
  rank: string;
  controlMode: ControlMode;
  type: DesignationType;
  terminal: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Designation name is required'),
  rank: Yup.string()
    .required('Rank is required')
    .matches(/^[1-9]\d*$/, 'Rank must be a positive number'),
});

/** Design-matched field styling (slate-300 border, soft shadow, focus ring). */
const INPUT =
  'w-full rounded-lg border border-[#cbd5e1] bg-white px-[15px] py-[11px] text-sm text-ink shadow-card placeholder:text-[#94a3b8] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

/** Uppercase field label with optional required marker. */
function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-semibold uppercase tracking-[0.6px] text-[#334155]">
      {children}
      {required && <span className="font-semibold text-[#f43f5e]"> *</span>}
    </label>
  );
}

const DEFAULTS = (defaultRank: number): DesignationForm => ({
  name: '',
  rank: String(defaultRank),
  controlMode: 'None',
  type: 'Official',
  terminal: false,
});

/** Form values for a fresh draft, or prefilled from an existing designation (edit mode). */
const formValues = (initial: NewDesignation | null | undefined, defaultRank: number): DesignationForm =>
  initial
    ? {
        name: initial.name,
        rank: String(initial.rank),
        controlMode: initial.controlMode,
        type: initial.type,
        terminal: initial.terminal,
      }
    : DEFAULTS(defaultRank);

export default function AddDesignationModal({
  open,
  defaultRank,
  initial,
  onCancel,
  onAdd,
}: {
  open: boolean;
  /** Pre-filled rank (next seniority slot) when adding. */
  defaultRank: number;
  /** When set, the modal edits this designation instead of adding a new one. */
  initial?: NewDesignation | null;
  onCancel: () => void;
  onAdd: (designation: NewDesignation) => void;
}) {
  const isEditing = Boolean(initial);
  const formik = useFormik<DesignationForm>({
    initialValues: formValues(initial, defaultRank),
    validationSchema,
    onSubmit: (values) => {
      onAdd({
        name: values.name.trim(),
        rank: parseInt(values.rank, 10),
        controlMode: values.controlMode,
        type: values.type,
        terminal: values.terminal,
      });
    },
  });
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
  const errorOf = (f: keyof DesignationForm) =>
    touched[f] && errors[f] ? (errors[f] as string) : undefined;

  // Reset the draft (fresh, or prefilled for edit) each time the modal opens.
  useEffect(() => {
    if (open) formik.resetForm({ values: formValues(initial, defaultRank) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultRank, initial]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      centered
      width={576}
      destroyOnHidden
      styles={{
        container: { padding: 0, borderRadius: 16, overflow: 'hidden' },
        body: { padding: 0 },
        mask: { background: 'rgba(11,28,48,0.2)', backdropFilter: 'blur(2px)' },
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-[#f1f5f9] px-6 pb-[17px] pt-6">
        <div className="flex flex-col gap-1 pr-6">
          <div className="flex items-center gap-2">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#eff6ff] text-brand">
              <AddDesignationIcon className="size-4" />
            </span>
            <h2 className="text-xl font-bold tracking-[-0.5px] text-[#0f172a]">
              {isEditing ? 'Edit Designation' : 'Add Designation'}
            </h2>
          </div>
          <p className="pl-10 text-xs leading-[19.5px] text-[#64748b]">
            Define designation title, numeric rank, authority, and status.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="rounded-lg p-1.5 text-[#94a3b8] transition-colors hover:bg-slate-100 hover:text-ink"
        >
          <CloseIcon className="size-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-5 px-6 py-5">
        {/* Designation name + Rank */}
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <FieldLabel required>Designation Name</FieldLabel>
            <input
              name="name"
              className={[INPUT, errorOf('name') ? '!border-red-400 focus:!border-red-500' : ''].join(' ')}
              placeholder="e.g. Revenue Inspector / Naib Tahsildar"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              autoFocus
            />
            {errorOf('name') ? (
              <p className="text-[11px] font-medium text-[#ba1a1a]">{errorOf('name')}</p>
            ) : (
              <p className="text-[11px] leading-[13.75px] text-[#64748b]">
                Official post designation title.
              </p>
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <FieldLabel required>Rank</FieldLabel>
              <span className="text-[11px] font-semibold leading-4 text-brand">Seniority Order</span>
            </div>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-[#94a3b8]">
                #
              </span>
              <input
                name="rank"
                type="number"
                min={1}
                className={[
                  INPUT,
                  'pl-7 font-semibold text-[#1e293b]',
                  errorOf('rank') ? '!border-red-400 focus:!border-red-500' : '',
                ].join(' ')}
                value={values.rank}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            {errorOf('rank') && (
              <p className="text-[11px] font-medium text-[#ba1a1a]">{errorOf('rank')}</p>
            )}
          </div>
        </div>

        {/* Authority + Control Mode */}
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="form-field-select flex min-w-0 flex-1 flex-col gap-1.5">
            <FieldLabel required>Authority</FieldLabel>
            <Select
              className="w-full"
              size="large"
              value={values.type}
              onChange={(v) => setFieldValue('type', v)}
              options={DESIGNATION_TYPES.map((t) => ({ value: t, label: t }))}
            />
          </div>
          <div className="form-field-select flex min-w-0 flex-1 flex-col gap-1.5">
            <FieldLabel required>Control Mode</FieldLabel>
            <Select
              className="w-full"
              size="large"
              value={values.controlMode}
              onChange={(v) => setFieldValue('controlMode', v)}
              options={CONTROL_MODES.map((c) => ({ value: c, label: c }))}
            />
          </div>
        </div>

        {/* Lowest level / terminal post toggle */}
        <div className="flex items-center justify-between gap-4 rounded-xl border border-[rgba(226,232,240,0.9)] bg-[#f8fafc] p-[15px]">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#1e293b]">
                Lowest Level / Terminal Post
              </span>
              <span className="rounded-full bg-[#d1fae5] px-2 py-0.5 text-[10px] font-semibold leading-[15px] text-[#065f46]">
                Hierarchy Base
              </span>
            </div>
            <p className="text-xs leading-4 text-[#64748b]">
              Is this the lowest administrative post with no officers ranked below it?
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={values.terminal}
            onClick={() => setFieldValue('terminal', !values.terminal)}
            className={[
              'relative h-6 w-11 shrink-0 rounded-full transition-colors',
              values.terminal ? 'bg-[#059669]' : 'bg-[#c2c6d6]',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-0.5 size-5 rounded-full bg-white transition-all',
                values.terminal ? 'left-[22px]' : 'left-0.5',
              ].join(' ')}
            />
          </button>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 border-t border-[#f1f5f9] pt-1.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-[#cbd5e1] bg-white px-[17px] py-[11px] text-xs font-medium text-[#334155] shadow-card transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => formik.submitForm()}
            className="rounded-lg bg-brand px-5 py-2.5 text-xs font-semibold text-white shadow-card transition-colors hover:bg-brand-dark"
          >
            {isEditing ? 'Save Changes' : 'Add Designation'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
